import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { Trash2, Copy, Clock, Check, X } from "lucide-react";
import { adminApi } from "@/lib/api/admin";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/useToast";

interface Invitation {
  id: string;
  email: string;
  code: string;
  expiresAt: string;
  usedAt: string | null;
}

type StatusInfo = {
  label: string;
  variant: "default" | "secondary" | "destructive";
  Icon: typeof Check;
};

function getStatus(inv: Invitation): StatusInfo {
  if (inv.usedAt) return { label: "Used", variant: "secondary", Icon: Check };
  if (new Date(inv.expiresAt) < new Date())
    return { label: "Expired", variant: "destructive", Icon: X };
  return { label: "Pending", variant: "default", Icon: Clock };
}

function buildActivationUrl(email: string, code: string): string {
  const baseUrl = window.location.origin;
  return `${baseUrl}/activate?email=${encodeURIComponent(email)}&code=${code}`;
}

export function InvitationList() {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const { toast } = useToast();

  const { data, isLoading, error } = useQuery({
    queryKey: ["invitations"],
    queryFn: () => adminApi.listInvitations(),
  });

  const revokeInvitation = useMutation({
    mutationFn: adminApi.revokeInvitation,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["invitations"] }),
  });

  const copyActivationUrl = async (email: string, code: string) => {
    try {
      // navigator.clipboard is absent outside a secure context (plain HTTP on a
      // non-localhost origin), so probe before touching .writeText.
      const clipboard = navigator.clipboard as Clipboard | undefined;
      if (typeof clipboard?.writeText !== "function") {
        throw new Error("Clipboard API unavailable");
      }
      await clipboard.writeText(buildActivationUrl(email, code));
      toast("Activation link copied to clipboard");
    } catch {
      toast(
        "Couldn't copy the activation link. Select it from the field below and copy it manually."
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        Loading invitations...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8 text-destructive">
        Failed to load invitations
      </div>
    );
  }

  const invitations = data?.invitations ?? [];

  if (invitations.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        No invitations found. Click "Invite User" to create one.
      </div>
    );
  }

  return (
    <ul className="list-none space-y-2">
      {invitations.map((inv: Invitation) => {
        const status = getStatus(inv);
        const isPending = status.label === "Pending";
        return (
          <li
            key={inv.id}
            className={`rounded-lg p-4 ${isMobile ? "space-y-3 bg-white/[0.03]" : "border flex items-center justify-between"}`}
          >
            <div className="space-y-1 min-w-0">
              <p className="font-medium truncate">{inv.email}</p>
              <p className="text-sm text-muted-foreground">
                Code: <span className="font-mono">{inv.code}</span>
                {" • "}
                {new Date(inv.expiresAt) > new Date()
                  ? `Expires ${formatDistanceToNow(new Date(inv.expiresAt), { addSuffix: true })}`
                  : `Expired ${formatDistanceToNow(new Date(inv.expiresAt), { addSuffix: true })}`}
              </p>
              {isPending && (
                <Input
                  readOnly
                  value={buildActivationUrl(inv.email, inv.code)}
                  aria-label={`Activation link for ${inv.email}`}
                  className="font-mono text-xs"
                  onFocus={(e) => {
                    e.currentTarget.select();
                  }}
                />
              )}
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={status.variant}>
                <status.Icon className="mr-1 h-3 w-3" />
                {status.label}
              </Badge>
              {isPending && (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      void copyActivationUrl(inv.email, inv.code);
                    }}
                    title="Copy activation link"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => revokeInvitation.mutate(inv.id)}
                    disabled={revokeInvitation.isPending}
                    title="Revoke invitation"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
